import type { RawTypedNativeModules, TypedNativeModules } from "../types/NativeModulesTypes";

type BridgeMethodProxyHandler = ProxyHandler<
  Required<RawTypedNativeModules>[keyof Required<RawTypedNativeModules>]
>;

const bridgeProxies: Map<string, TypedNativeModules[keyof TypedNativeModules]> = new Map();

const bridgeProxyHandler: ProxyHandler<RawTypedNativeModules> = {
  get(target, prop) {
    // Regenerator runtime may check if this object is a thenable
    if (prop === "then") {
      return undefined;
    }
    if (typeof prop !== "string") {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const bridge = target[prop as keyof typeof target] ?? {};
    if (!bridgeProxies.has(prop)) {
      bridgeProxies.set(
        prop,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        new Proxy(bridge, bridgeMethodProxyHandler) as TypedNativeModules[keyof TypedNativeModules],
      );
    }
    return bridgeProxies.get(prop);
  },
};

const bridgeMethodProxyHandler: BridgeMethodProxyHandler = {
  get(target, prop) {
    // Regenerator runtime may check if this object is a thenable
    if (prop === "then") {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    const method = target[prop as keyof typeof target];

    if (method === undefined) {
      return typeof prop === "string" && prop.endsWith("Sync")
        ? () => null
        : () => {
            return Promise.resolve(null);
          };
    }

    return method;
  },
};

/** Stubs methods an older native host lacks, matching the per-bridge behavior of proxyBridges. */
export function proxyBridgeMethods<K extends keyof TypedNativeModules>(
  bridge: RawTypedNativeModules[K] | null | undefined,
): TypedNativeModules[K] {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- missing methods are stubbed by the proxy handler
  return new Proxy(bridge ?? {}, bridgeMethodProxyHandler) as TypedNativeModules[K];
}

export function proxyBridges(bridges: RawTypedNativeModules): TypedNativeModules {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  return new Proxy(bridges, bridgeProxyHandler) as TypedNativeModules;
}

export function __resetProxiesForTestEnvironment() {
  bridgeProxies.clear();
}
